import React, { useEffect, useRef, useState } from 'react';

const CX = 110; const CY = 110; const R = 84; const START = 135; const SWEEP = 270;
const GRADE_COLOR = { A: '#3DD68C', B: '#A3E063', C: '#F5B942', D: '#FF9A4D', F: '#FF6B6B' };
const pt = (deg, r) => { const a = (deg * Math.PI) / 180; return [CX + r * Math.cos(a), CY + r * Math.sin(a)]; };
const [ax, ay] = pt(START, R); const [bx, by] = pt(START + SWEEP, R);
const ARC = `M${ax.toFixed(2)} ${ay.toFixed(2)} A${R} ${R} 0 1 1 ${bx.toFixed(2)} ${by.toFixed(2)}`;
const TICKS = Array.from({ length: 21 }, (_, i) => {
  const d = START + (SWEEP * i) / 20; const major = i % 5 === 0;
  const [x1, y1] = pt(d, R + 12); const [x2, y2] = pt(d, R + (major ? 20 : 16));
  return { x1, y1, x2, y2, major };
});
const reduced = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function gradeColor(grade) { return GRADE_COLOR[grade] || GRADE_COLOR.F; }

export default function ScoreDial({ score = 0, grade = 'F', size = 200, animate = true, label = 'out of 100', compact = false }) {
  const [value, setValue] = useState(animate && !reduced() ? 0 : score);
  const frame = useRef(null);
  useEffect(() => {
    if (!animate || reduced()) { setValue(score); return undefined; }
    let t0 = null;
    const tick = (ts) => {
      if (t0 === null) t0 = ts;
      const t = Math.min(1, (ts - t0) / 1700);
      const spring = 1 - Math.exp(-6.2 * t) * Math.cos(9.5 * t);
      setValue(t < 1 ? score * spring : score);
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [score, animate]);
  const v = Math.max(0, Math.min(100, value));
  const [kx, ky] = pt(START + (SWEEP * v) / 100, R);
  const color = gradeColor(grade);
  return (
    <div className="relative aspect-square max-w-full" style={{ width: size }}>
      <svg viewBox="0 0 220 220" className="block h-full w-full" role="img" aria-label={`Score ${score} out of 100, grade ${grade}`}>
        {TICKS.map((t, i) => <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={t.major ? 'rgba(154,167,187,.55)' : 'rgba(154,167,187,.25)'} strokeWidth={t.major ? 1.6 : 1} />)}
        <path d={ARC} fill="none" stroke="rgba(154,167,187,.13)" strokeWidth="14" strokeLinecap="round" />
        <path d={ARC} pathLength="100" fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" strokeDasharray="100 100" strokeDashoffset={100 - v} />
        <circle cx={kx} cy={ky} r="9" fill="#060A12" stroke={color} strokeWidth="4" />
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">
        <strong className={`font-display font-bold tabular-nums leading-none tracking-tight ${compact ? 'text-3xl' : 'text-6xl'}`}>{Math.round(v)}</strong>
        <span className="mt-1 text-xs text-dim">{compact ? grade : label}</span>
      </div>
    </div>
  );
}
